import { useEffect, useMemo, useState } from "react";
import type { Key, ReactElement } from "react";
import { Button, Form, Image, Input, InputNumber, Modal, Radio, Select, Space, Switch, Table, Tag, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { batchAddCatalog, deleteCatalog, listCatalog, saveCatalog } from "@/api/shopCatalog";
import type { CatalogKind, CatalogRecord, CatalogWrite } from "@/types/shopCatalog";
import { formatDateTime } from "@/utils/dateTime";
import { nonNegativeRules } from "../numericValidation";
import { ProductImageInput } from "../product/ProductImageInput";

interface CatalogManagerProps {
  kind: CatalogKind;
  parentSpec?: CatalogRecord;
  onManageValues?: (record: CatalogRecord) => void;
}
interface Filters { name?: string; status?: number }
interface EditorValues { name: string; image?: string; sort: number; status: number; remark?: string }
const statuses = [{ label: "启用", value: 1 }, { label: "禁用", value: 0 }];
const settings = {
  category: { title: "分类", name: "categoryName", image: "categoryIcon" },
  spec: { title: "规格项", name: "specName", image: undefined },
  specValue: { title: "规格值", name: "specValue", image: "specImage" },
} as const;

export function CatalogManager({ kind, parentSpec, onManageValues }: CatalogManagerProps): ReactElement {
  const config = settings[kind];
  const [searchForm] = Form.useForm<Filters>();
  const [editorForm] = Form.useForm<EditorValues>();
  const [batchForm] = Form.useForm<{ rows: EditorValues[] }>();
  const [rows, setRows] = useState<CatalogRecord[]>([]);
  const [filters, setFilters] = useState<Filters>({});
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([]);
  const [editing, setEditing] = useState<CatalogRecord | null>(null);
  const [open, setOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [page, setPage] = useState(1);
  const specId = parentSpec?.id;
  const locked = busy || uploading;
  const handleRefresh = (): void => { setSelectedKeys([]); setRevision((value) => value + 1); };
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    void listCatalog(kind, specId, controller.signal).then(({ data }) => {
      if (!controller.signal.aborted) setRows(data ?? []);
    }).catch(() => {
      if (!controller.signal.aborted) setRows([]);
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return (): void => controller.abort();
  }, [kind, specId, revision]);
  const filteredRows = useMemo(() => rows.filter((row) =>
    (!filters.name || (row[config.name] ?? "").includes(filters.name)) &&
    (filters.status === undefined || row.status === filters.status)
  ), [rows, filters, config.name]);
  const handleSearch = (values: Filters): void => {
    setFilters({ name: values.name?.trim(), status: values.status });
    setPage(1); handleRefresh();
  };
  const handleReset = (): void => { searchForm.resetFields(); handleSearch({}); };
  const handleAdd = (): void => {
    setEditing(null); editorForm.resetFields(); setOpen(true);
  };
  const handleEdit = (record: CatalogRecord): void => {
    setEditing(record);
    editorForm.setFieldsValue({ name: record[config.name] ?? "", image: config.image ? record[config.image] ?? "" : "", sort: record.sort, status: record.status, remark: record.remark ?? "" });
    setOpen(true);
  };
  const payload = (values: EditorValues): CatalogWrite => ({
    [config.name]: values.name.trim(),
    ...(config.image ? { [config.image]: values.image?.trim() ?? "" } : {}),
    ...(kind === "specValue" ? { specId } : {}),
    sort: values.sort, status: values.status, remark: values.remark?.trim() ?? "",
  });
  const handleSave = async (values: EditorValues): Promise<void> => {
    if (locked) return;
    setBusy(true);
    try {
      await saveCatalog(kind, { ...payload(values), ...(editing ? { id: editing.id } : {}) });
      message.success("保存成功"); setOpen(false); handleRefresh();
    } catch { /* 请求层展示错误，保留表单。 */ } finally { setBusy(false); }
  };
  const handleBatchSave = async (values: { rows: EditorValues[] }): Promise<void> => {
    if (locked || !values.rows.length) return;
    setBusy(true);
    try {
      await batchAddCatalog(kind, values.rows.map(payload));
      message.success("批量新增成功"); setBatchOpen(false); handleRefresh();
    } catch { /* 请求层展示错误，保留表单。 */ } finally { setBusy(false); }
  };
  const handleDelete = (records: CatalogRecord[]): void => {
    if (!records.length || locked) return;
    const note = kind === "category" ? "已被商品使用的分类不能删除。" : kind === "spec" ? "请先删除该规格项下的规格值。" : "删除已被商品引用的规格值会导致 SKU 无法显示对应名称，请先调整商品规格。";
    Modal.confirm({
      title: `删除${config.title}`,
      content: `确认删除 ${records.length} 条${config.title}？${note}`,
      onOk: async (): Promise<void> => {
        setBusy(true);
        try { await deleteCatalog(kind, records.map((row) => row.id)); message.success("删除成功"); handleRefresh(); }
        finally { setBusy(false); }
      },
    });
  };
  const handleStatus = async (record: CatalogRecord): Promise<void> => {
    if (locked) return;
    setBusy(true);
    try {
      await saveCatalog(kind, { ...payload({ name: record[config.name] ?? "", image: config.image ? record[config.image] : undefined, sort: record.sort, status: record.status === 1 ? 0 : 1, remark: record.remark }), id: record.id });
      message.success("状态已更新"); handleRefresh();
    } catch { /* 请求层展示错误。 */ } finally { setBusy(false); }
  };
  const handleCategoryStatusChange = (record: CatalogRecord): void => {
    if (locked || loading) return;
    if (record.status === 1) {
      Modal.confirm({
        title: "禁用分类",
        content: `确认禁用分类“${record.categoryName ?? ""}”？`,
        okText: "确认禁用",
        cancelText: "取消",
        onOk: () => handleStatus(record),
      });
    } else {
      void handleStatus(record);
    }
  };
  const columns: ColumnsType<CatalogRecord> = [
    { title: "ID", dataIndex: "id", width: 90 },
    { title: `${config.title}名称`, dataIndex: config.name, width: 180 },
    ...(config.image ? [{ title: kind === "category" ? "分类图标" : "规格图片", dataIndex: config.image, width: 100, render: (value: string): ReactElement | string => value ? <Image src={value} width={48} height={48} style={{ objectFit: "cover" }} /> : "-" }] : []),
    { title: "排序", dataIndex: "sort", width: 90 },
    { title: "状态", dataIndex: "status", width: 90, render: (value: number, record: CatalogRecord): ReactElement => kind === "category"
      ? <Switch checked={value === 1} checkedChildren="启用" unCheckedChildren="禁用" aria-label={`${record.categoryName ?? "分类"}状态`} disabled={locked || loading} onChange={() => handleCategoryStatusChange(record)} />
      : <Tag color={value === 1 ? "green" : "default"}>{value === 1 ? "启用" : "禁用"}</Tag> },
    { title: "备注", dataIndex: "remark", width: 160 },
    { title: "创建时间", dataIndex: "createTime", render: formatDateTime, width: 180 },
    { title: "更新时间", dataIndex: "updateTime", render: formatDateTime, width: 180 },
    { title: "操作", key: "actions", fixed: "right", width: onManageValues ? 300 : kind === "category" ? 150 : 210, render: (_: unknown, record: CatalogRecord): ReactElement => <Space>
      {onManageValues && <Button size="small" onClick={() => onManageValues(record)}>规格值</Button>}
      <Button size="small" disabled={locked || loading} onClick={() => handleEdit(record)}>修改</Button>
      {kind !== "category" && <Button size="small" disabled={locked || loading} onClick={() => void handleStatus(record)}>{record.status === 1 ? "禁用" : "启用"}</Button>}
      <Button size="small" danger disabled={locked || loading} onClick={() => handleDelete([record])}>删除</Button>
    </Space> },
  ];
  const editorFields = (prefix?: number): ReactElement => <>
    <Form.Item name={prefix === undefined ? "name" : [prefix, "name"]} label={`${config.title}名称`} rules={[{ required: true, whitespace: true, message: `请输入${config.title}名称` }]}><Input /></Form.Item>
    {config.image && <Form.Item name={prefix === undefined ? "image" : [prefix, "image"]} label={kind === "category" ? "分类图标" : "规格图片"}><ProductImageInput disabled={locked} onBusyChange={setUploading} /></Form.Item>}
    <Form.Item name={prefix === undefined ? "sort" : [prefix, "sort"]} label="排序" rules={nonNegativeRules("排序", true, true)}><InputNumber min={0} precision={0} /></Form.Item>
    <Form.Item name={prefix === undefined ? "status" : [prefix, "status"]} label="状态" rules={[{ required: true }]}><Radio.Group options={statuses} /></Form.Item>
    <Form.Item name={prefix === undefined ? "remark" : [prefix, "remark"]} label="备注"><Input /></Form.Item>
  </>;
  return <div>
    <Form name={`${kind}-search`} form={searchForm} layout="inline" onFinish={handleSearch} style={{ gap: 12, marginBottom: 16 }}>
      <Form.Item name="name" label={`${config.title}名称`}><Input allowClear /></Form.Item>
      <Form.Item name="status" label="状态"><Select allowClear options={statuses} style={{ width: 140 }} /></Form.Item>
      <Space><Button type="primary" htmlType="submit">查询</Button><Button onClick={handleReset}>重置</Button></Space>
    </Form>
    <Space wrap style={{ marginBottom: 16 }}>
      <Button type="primary" disabled={locked} onClick={handleAdd}>新增{config.title}</Button>
      <Button disabled={locked} onClick={() => { batchForm.resetFields(); setBatchOpen(true); }}>批量新增</Button>
      <Button danger disabled={locked || loading || !selectedKeys.length} onClick={() => handleDelete(filteredRows.filter((row) => selectedKeys.includes(row.id)))}>批量删除</Button>
    </Space>
    <Table<CatalogRecord> rowKey="id" bordered size="small" loading={loading} columns={columns} dataSource={filteredRows} scroll={{ x: "max-content" }}
      rowSelection={{ selectedRowKeys: selectedKeys, onChange: setSelectedKeys, getCheckboxProps: () => ({ disabled: loading || locked }) }}
      pagination={{ current: Math.min(page, Math.max(1, Math.ceil(filteredRows.length / 10))), pageSize: 10, onChange: setPage, showSizeChanger: false, total: filteredRows.length }} />
    <Modal forceRender open={open} title={`${editing ? "编辑" : "新增"}${config.title}`} width={560} onCancel={() => { if (!locked) setOpen(false); }} onOk={() => editorForm.submit()} confirmLoading={busy} okButtonProps={{ disabled: uploading }} cancelButtonProps={{ disabled: locked }} maskClosable={!locked} closable={!locked}>
      <Form name={`${kind}-editor`} form={editorForm} layout="vertical" initialValues={{ name: "", sort: 0, status: 1 }} onFinish={handleSave} disabled={locked}>
        {editorFields()}
      </Form>
    </Modal>
    <Modal forceRender open={batchOpen} title={`批量新增${config.title}`} width={680} onCancel={() => { if (!locked) setBatchOpen(false); }} onOk={() => batchForm.submit()} confirmLoading={busy} okButtonProps={{ disabled: uploading }} cancelButtonProps={{ disabled: locked }} maskClosable={!locked} closable={!locked}>
      <Form name={`${kind}-batch`} form={batchForm} layout="vertical" initialValues={{ rows: [{ name: "", sort: 0, status: 1 }] }} onFinish={handleBatchSave} disabled={locked}>
        <Form.List name="rows">{(fields, { add, remove }) => <>
          {fields.map((field) => <div key={field.key} style={{ padding: 16, marginBottom: 12, border: "1px solid #eee", borderRadius: 6 }}>
            {editorFields(field.name)}<Button danger disabled={locked || fields.length === 1} onClick={() => remove(field.name)}>移除</Button>
          </div>)}
          <Button onClick={() => add({ name: "", sort: 0, status: 1 })}>添加一行</Button>
        </>}</Form.List>
      </Form>
    </Modal>
  </div>;
}
